// src/components/UserFiles/UserFileSignaturesDialog.jsx

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ListItemAvatar,
  Divider,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

import { fetchFileSignatures } from '../../../services/fileSignaturesService';

const UserFileSignaturesDialog = ({ open, onClose, file, t }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSignatures = async () => {
      if (!open || !file) return;
      setLoading(true);
      setError(null);

      try {
        const res = await fetchFileSignatures({
          contentType: 'accounts.userfile',
          objectId: file.id,
        });

        if (res.success) {
          setSignatures(res.data || []);
        } else {
          const msg =
            res.error ||
            t('files.signatures_load_failed', 'Nepodarilo sa načítať podpisy.');
          setError(msg);
          enqueueSnackbar(msg, { variant: 'error' });
        }
      } catch (e) {
        console.error('Error loading signatures', e);
        const msg = t(
          'files.signatures_load_failed',
          'Nepodarilo sa načítať podpisy.'
        );
        setError(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadSignatures();
  }, [open, file, t, enqueueSnackbar]);

  const handleClose = () => {
    if (loading) return;
    setError(null);
    setSignatures([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          backgroundImage: 'none', // Prevents weird overlaps in some dark mode setups
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.default', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('files.signatures_dialog_title', 'Podpisy súboru')}
        </Typography>
        <IconButton 
          onClick={handleClose} 
          size="small"
          disabled={loading}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, px: { xs: 2, md: 3 } }}>
        {file && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.selected', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, fontWeight: 600 }}
            >
              {t('files.signatures_for_file', 'Podpisy pre súbor:')}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontWeight: 500, mt: 0.5, wordBreak: 'break-all' }}
            >
              {file.filename}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : signatures.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4, 
              bgcolor: 'background.default', 
              borderRadius: 2, 
              border: '1px dashed', 
              borderColor: 'divider' 
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('files.no_signatures', 'Tento súbor zatiaľ nemá žiadne podpisy.')}
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <List disablePadding>
              {signatures.map((sig, index) => {
                const userLabel =
                  sig.username ||
                  t('files.unknown_user', 'Neznámy používateľ');

                const signedAtText = sig.signed_at
                  ? new Date(sig.signed_at).toLocaleString()
                  : t('files.unknown_date', 'Neznámy dátum');

                return (
                  <React.Fragment key={sig.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 2,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: 14,
                            bgcolor: 'action.selected',
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {userLabel}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {t(
                              'files.signed_at',
                              'Podpísané dňa {{date}}',
                              { date: signedAtText }
                            )}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < signatures.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          disabled={loading}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {t('common.close', 'Zavrieť')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFileSignaturesDialog;